import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Icon } from "./Icon";

interface RateLimitConfig {
  enabled: boolean;
  windowSeconds: number;
  maxMessages: number;
  cooldownSeconds: number;
  adaptiveEnabled: boolean;
  bufferMessages: boolean;
  summarizeBuffered: boolean;
  usePercentageMode: boolean;
  responsePercentage: number;
}

interface RateLimitStats {
  messagesSent: number;
  messagesThrottled: number;
  messagesBuffered: number;
  summariesSent: number;
  windowViolations: number;
  timestamp: string;
}

interface RateLimitControlProps {
  token: string;
  userId: string;
  userType?: string;
}

export const RateLimitControl: React.FC<RateLimitControlProps> = ({
  token,
  userId,
  userType = "free",
}) => {
  const isPremium =
    userType === "premium" || userType === "vip" || userType === "admin";
  const location = useLocation();

  const [config, setConfig] = useState<RateLimitConfig>({
    enabled: true,
    windowSeconds: 60,
    maxMessages: 5,
    cooldownSeconds: 30,
    adaptiveEnabled: false,
    bufferMessages: true,
    summarizeBuffered: true,
    usePercentageMode: false,
    responsePercentage: 100,
  });
  const [originalConfig, setOriginalConfig] = useState<RateLimitConfig>({
    enabled: true,
    windowSeconds: 60,
    maxMessages: 5,
    cooldownSeconds: 30,
    adaptiveEnabled: false,
    bufferMessages: true,
    summarizeBuffered: true,
    usePercentageMode: false,
    responsePercentage: 100,
  });
  const [stats, setStats] = useState<RateLimitStats[]>([]);
  const [totals, setTotals] = useState({
    messagesSent: 0,
    messagesThrottled: 0,
    messagesBuffered: 0,
    summariesSent: 0,
    windowViolations: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [statsHours, setStatsHours] = useState(24);
  const [status, setStatus] = useState<{
    responseWindowProgress: number;
    percentageLimitProgress: number;
    cooldownProgress: number;
    isInCooldown: boolean;
    cooldownEndsAt?: string;
    currentResponses: number;
    maxResponses: number;
    windowSeconds: number;
    currentPercentage: number;
    targetPercentage: number;
    usePercentageMode: boolean;
    totalChannelMessages: number;
  }>({
    responseWindowProgress: 0,
    percentageLimitProgress: 0,
    cooldownProgress: 0,
    isInCooldown: false,
    currentResponses: 0,
    maxResponses: 5,
    windowSeconds: 60,
    currentPercentage: 0,
    targetPercentage: 100,
    usePercentageMode: false,
    totalChannelMessages: 0,
  });

  // Check if config has changed
  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

  // Clear error/success when config changes
  useEffect(() => {
    if (hasChanges) {
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [config]);

  // Fetch current configuration
  useEffect(() => {
    fetchConfig();
    fetchStats();
    fetchStatus();
  }, [token]);

  // Refresh stats when hours change
  useEffect(() => {
    fetchStats();
  }, [statsHours]);

  // Refresh status and stats every 2 seconds only when component is visible
  useEffect(() => {
    // Only poll when on the rate limiting page
    if (location.pathname !== "/dashboard/control/rate-limiting") {
      return;
    }

    const interval = setInterval(() => {
      fetchStatus();
      fetchStats();
    }, 2000);

    return () => clearInterval(interval);
  }, [location.pathname, token, userId, statsHours]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/bot/${userId}/rate-limit/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        // If user is not premium, force disable premium features
        if (!isPremium) {
          data.adaptiveEnabled = false;
          data.bufferMessages = false;
          data.summarizeBuffered = false;
          data.usePercentageMode = false;
        }
        setConfig(data);
        setOriginalConfig(data);
      }
    } catch (error) {
      console.error("Failed to fetch rate limit config:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `/api/bot/${userId}/rate-limit/stats?hours=${statsHours}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        console.log("Rate limit stats response:", data);
        setStats(data.stats || []);
        setTotals(
          data.totals || {
            messagesSent: 0,
            messagesThrottled: 0,
            messagesBuffered: 0,
            summariesSent: 0,
            windowViolations: 0,
          },
        );
      }
    } catch (error) {
      console.error("Failed to fetch rate limit stats:", error);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await fetch(`/api/bot/${userId}/rate-limit/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log("Rate limit status received:", data);
        setStatus(data);
      }
    } catch (error) {
      console.error("Failed to fetch rate limit status:", error);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      // Ensure free users can't save premium features as enabled
      const configToSave = { ...config };
      if (!isPremium) {
        configToSave.adaptiveEnabled = false;
        configToSave.bufferMessages = false;
        configToSave.summarizeBuffered = false;
        configToSave.usePercentageMode = false;
      }

      const response = await fetch(`/api/bot/${userId}/rate-limit/config`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(configToSave),
      });

      if (response.ok) {
        const updated = await response.json();
        setConfig(updated);
        setOriginalConfig(updated);
        setSaveSuccess(true);
        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        const errorText = await response.text();
        setSaveError(
          `Failed to save: ${response.status} - ${errorText || response.statusText}`,
        );
      }
    } catch (error) {
      console.error("Failed to save rate limit config:", error);
      setSaveError(
        `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#1a202c",
        padding: "24px",
        borderRadius: "12px",
        marginBottom: "20px",
        marginTop: "20px",
        border: "1px solid #4a5568",
      }}
    >
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <h3
            style={{
              color: "#e2e8f0",
              fontSize: "20px",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <span>
              <Icon name="zap" size={20} />
            </span>{" "}
            Rate Limiting
          </h3>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              cursor: "pointer",
            }}
          >
            <span style={{ color: "#cbd5e0", fontSize: "14px" }}>
              {saving ? "Updating..." : config.enabled ? "Enabled" : "Disabled"}
            </span>
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={async (e) => {
                const newEnabled = e.target.checked;
                const newConfig = { ...config, enabled: newEnabled };

                // Ensure free users can't have premium features enabled
                if (!isPremium) {
                  newConfig.adaptiveEnabled = false;
                  newConfig.bufferMessages = false;
                  newConfig.summarizeBuffered = false;
                  newConfig.usePercentageMode = false;
                }

                setConfig(newConfig);

                // Immediately save the enabled/disabled state
                try {
                  setSaving(true);
                  setSaveError(null);
                  setSaveSuccess(false);

                  const response = await fetch(
                    `/api/bot/${userId}/rate-limit/config`,
                    {
                      method: "PUT",
                      headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify(newConfig),
                    },
                  );

                  if (response.ok) {
                    const updated = await response.json();
                    setConfig(updated);
                    setOriginalConfig(updated);
                    setSaveSuccess(true);
                    setTimeout(() => setSaveSuccess(false), 3000);
                  } else {
                    const errorText = await response.text();
                    setSaveError(
                      `Failed to ${newEnabled ? "enable" : "disable"} rate limiting`,
                    );
                    // Revert on error
                    setConfig(config);
                  }
                } catch (error) {
                  console.error("Failed to toggle rate limiting:", error);
                  setSaveError(`Network error`);
                  // Revert on error
                  setConfig(config);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              style={{
                width: "50px",
                height: "24px",
                appearance: "none",
                backgroundColor: config.enabled ? "#9146FF" : "#4a5568",
                borderRadius: "12px",
                position: "relative",
                cursor: saving ? "not-allowed" : "pointer",
                transition: "background-color 0.3s",
                outline: "none",
                opacity: saving ? 0.7 : 1,
              }}
            />
            <style>{`
            input[type="checkbox"]:after {
              content: "";
              position: absolute;
              width: 20px;
              height: 20px;
              border-radius: 50%;
              background: white;
              top: 2px;
              left: ${config.enabled ? "28px" : "2px"};
              transition: left 0.3s;
            }
          `}</style>
          </label>
        </div>
        {saveError && !hasChanges && (
          <div
            style={{
              fontSize: "12px",
              marginBottom: "10px",
              color: "#f56565",
              textAlign: "right",
            }}
          >
            {saveError}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ color: "#cbd5e0", textAlign: "center", padding: "20px" }}>
          Loading configuration...
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            {/* Message Window */}
            <div>
              <label
                style={{
                  color: "#cbd5e0",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                Response Window (seconds)
              </label>
              <input
                type="number"
                value={config.windowSeconds}
                onChange={(e) => {
                  const value = e.target.value;
                  setConfig({
                    ...config,
                    windowSeconds:
                      value === ""
                        ? 0
                        : parseInt(value) || config.windowSeconds,
                  });
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (e.target.value === "" || isNaN(value) || value < 10) {
                    setConfig({
                      ...config,
                      windowSeconds: 60,
                    });
                  } else if (value > 300) {
                    setConfig({
                      ...config,
                      windowSeconds: 300,
                    });
                  }
                }}
                disabled={!config.enabled}
                style={{
                  width: "calc(100% - 24px)",
                  padding: "8px 12px",
                  backgroundColor: "#2d3748",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  opacity: config.enabled ? 1 : 0.5,
                }}
                min="10"
                max="300"
                placeholder="10-300"
              />
            </div>

            {/* Max Messages */}
            <div>
              <label
                style={{
                  color: "#cbd5e0",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                Max Responses per Window
              </label>
              <input
                type="number"
                value={config.maxMessages}
                onChange={(e) => {
                  const value = e.target.value;
                  setConfig({
                    ...config,
                    maxMessages:
                      value === "" ? 0 : parseInt(value) || config.maxMessages,
                  });
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (e.target.value === "" || isNaN(value) || value < 1) {
                    setConfig({
                      ...config,
                      maxMessages: 5,
                    });
                  } else if (value > 20) {
                    setConfig({
                      ...config,
                      maxMessages: 20,
                    });
                  }
                }}
                disabled={!config.enabled}
                style={{
                  width: "calc(100% - 24px)",
                  padding: "8px 12px",
                  backgroundColor: "#2d3748",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  opacity: config.enabled ? 1 : 0.5,
                }}
                min="1"
                max="20"
                placeholder="1-20"
              />
            </div>

            {/* Cooldown */}
            <div>
              <label
                style={{
                  color: "#cbd5e0",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                Cooldown Period (seconds)
                <span
                  style={{
                    display: "block",
                    fontSize: "12px",
                    color: "#a0aec0",
                    fontWeight: "normal",
                    marginTop: "2px",
                  }}
                >
                  How long HAL stays silent after hitting the response limit
                </span>
              </label>
              <input
                type="number"
                value={config.cooldownSeconds}
                onChange={(e) => {
                  const value = e.target.value;
                  setConfig({
                    ...config,
                    cooldownSeconds:
                      value === ""
                        ? 0
                        : parseInt(value) || config.cooldownSeconds,
                  });
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value);
                  if (e.target.value === "" || isNaN(value) || value < 5) {
                    setConfig({
                      ...config,
                      cooldownSeconds: 30,
                    });
                  } else if (value > 600) {
                    setConfig({
                      ...config,
                      cooldownSeconds: 600,
                    });
                  }
                }}
                disabled={!config.enabled}
                style={{
                  width: "calc(100% - 24px)",
                  padding: "8px 12px",
                  backgroundColor: "#2d3748",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  opacity: config.enabled ? 1 : 0.5,
                }}
                min="5"
                max="600"
                placeholder="5-600"
              />
            </div>

            {/* Response Percentage */}
            <div>
              <label
                style={{
                  color: "#cbd5e0",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                Response Percentage: {config.responsePercentage}%
                <span
                  style={{
                    display: "block",
                    fontSize: "12px",
                    color: "#a0aec0",
                    fontWeight: "normal",
                    marginTop: "2px",
                  }}
                >
                  {config.responsePercentage === 100
                    ? "No percentage limit"
                    : `HAL responds to ~${config.responsePercentage}% of channel messages`}
                </span>
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={config.responsePercentage}
                  onChange={(e) => {
                    setConfig({
                      ...config,
                      responsePercentage: parseInt(e.target.value),
                      usePercentageMode: parseInt(e.target.value) < 100,
                    });
                  }}
                  disabled={!config.enabled}
                  style={{
                    width: "calc(100% - 24px)",
                    height: "6px",
                    borderRadius: "3px",
                    background: `linear-gradient(to right, #9146FF 0%, #9146FF ${(config.responsePercentage - 5) * 1.05}%, #4a5568 ${(config.responsePercentage - 5) * 1.05}%, #4a5568 100%)`,
                    outline: "none",
                    WebkitAppearance: "none",
                    cursor: config.enabled ? "pointer" : "not-allowed",
                    opacity: config.enabled ? 1 : 0.5,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "4px",
                    fontSize: "11px",
                    color: "#718096",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                  }}
                >
                  <span>5%</span>
                  <span>100%</span>
                </div>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#e2e8f0",
                    marginTop: "8px",
                    marginBottom: "0",
                    paddingLeft: "12px",
                    fontWeight: "500",
                  }}
                >
                  {config.responsePercentage === 100
                    ? "No Limit"
                    : config.responsePercentage <= 10
                      ? "Very Selective"
                      : config.responsePercentage <= 25
                        ? "Selective"
                        : config.responsePercentage <= 50
                          ? "Moderate"
                          : "Active"}
                </p>
              </div>
              <style>{`
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 20px;
                  height: 20px;
                  background: #9146FF;
                  cursor: pointer;
                  border-radius: 50%;
                  border: 2px solid #1a202c;
                }
                input[type="range"]::-moz-range-thumb {
                  width: 20px;
                  height: 20px;
                  background: #9146FF;
                  cursor: pointer;
                  border-radius: 50%;
                  border: 2px solid #1a202c;
                }
              `}</style>
            </div>
          </div>

          {/* Limit Explanation */}
          <div
            style={{
              backgroundColor: "rgba(145, 70, 255, 0.1)",
              border: "1px solid rgba(145, 70, 255, 0.3)",
              borderRadius: "6px",
              padding: "12px",
              marginBottom: "20px",
            }}
          >
            <p
              style={{
                fontSize: "12px",
                color: "#cbd5e0",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              <strong>How it works:</strong> HAL enters cooldown when{" "}
              <em>either</em> the response limit ({config.maxMessages} responses
              per {config.windowSeconds}s) <em>or</em> the percentage limit (
              {config.responsePercentage}%) is reached - whichever comes first.
              Set percentage to 100% to use only the response limit.
            </p>
          </div>

          {/* Advanced Options */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer",
                color: "#cbd5e0",
                fontSize: "14px",
              }}
            >
              <input
                type="checkbox"
                checked={config.bufferMessages}
                onChange={(e) => {
                  if (isPremium) {
                    const checked = e.target.checked;
                    setConfig({
                      ...config,
                      bufferMessages: checked,
                      summarizeBuffered: checked, // Always match bufferMessages
                    });
                  }
                }}
                disabled={!config.enabled || !isPremium}
                style={{
                  width: "18px",
                  height: "18px",
                  accentColor: "#9146FF",
                  opacity: config.enabled && isPremium ? 1 : 0.5,
                  cursor:
                    config.enabled && isPremium ? "pointer" : "not-allowed",
                }}
              />
              <span style={{ opacity: isPremium ? 1 : 0.7 }}>
                Intelligent message summarization after cooldown period
                {!isPremium && (
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "11px",
                      color: "#9146FF",
                      backgroundColor: "rgba(145, 70, 255, 0.2)",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontWeight: "600",
                    }}
                  >
                    PREMIUM
                  </span>
                )}
                <span
                  style={{
                    display: "block",
                    fontSize: "12px",
                    color: "#a0aec0",
                    fontWeight: "normal",
                    marginTop: "2px",
                    marginLeft: "28px",
                  }}
                >
                  Queues messages during rate limit and replies with intelligent
                  summary when cooldown ends
                </span>
              </span>
            </label>
          </div>

          {/* Save Button */}
          <div style={{ marginBottom: "20px" }}>
            <button
              onClick={saveConfig}
              disabled={!hasChanges || saving}
              style={{
                backgroundColor: saveSuccess
                  ? "#48bb78"
                  : saveError
                    ? "#e53e3e"
                    : "#9146FF",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: !hasChanges || saving ? "not-allowed" : "pointer",
                opacity: !hasChanges || saving ? 0.5 : 1,
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                if (hasChanges && !saving) {
                  e.currentTarget.style.backgroundColor = saveSuccess
                    ? "#38a169"
                    : saveError
                      ? "#dc2626"
                      : "#7c3aed";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = saveSuccess
                  ? "#48bb78"
                  : saveError
                    ? "#e53e3e"
                    : "#9146FF";
              }}
            >
              {saving ? (
                "Saving..."
              ) : saveSuccess ? (
                <>
                  <Icon
                    name="check"
                    size={14}
                    color="#10B981"
                    style={{ verticalAlign: "-2px", marginRight: "6px" }}
                  />
                  Saved Successfully
                </>
              ) : saveError ? (
                "Save Failed - Try Again"
              ) : hasChanges ? (
                "Save Configuration"
              ) : (
                "No Changes to Save"
              )}
            </button>
            {saveError && (
              <div
                style={{
                  color: "#f56565",
                  fontSize: "12px",
                  marginTop: "8px",
                  backgroundColor: "rgba(245, 101, 101, 0.1)",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  border: "1px solid rgba(245, 101, 101, 0.3)",
                }}
              >
                {saveError}
              </div>
            )}
          </div>

          {/* Progress Bars */}
          <h4
            style={{
              color: "#e2e8f0",
              fontSize: "16px",
              marginBottom: "16px",
              marginTop: 0,
            }}
          >
            Current Status
          </h4>
          <div
            style={{
              marginBottom: "20px",
              padding: "16px",
              backgroundColor: "#2d3748",
              borderRadius: "8px",
              border: "1px solid #4a5568",
            }}
          >
            {/* Response Limit Progress */}
            <div style={{ marginBottom: "16px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "4px",
                }}
              >
                <span style={{ color: "#cbd5e0", fontSize: "13px" }}>
                  Response Limit
                </span>
                <span style={{ color: "#a0aec0", fontSize: "12px" }}>
                  {status.currentResponses} / {status.maxResponses} responses /{" "}
                  {status.windowSeconds}s
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "8px",
                  backgroundColor: "#1a202c",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${status.responseWindowProgress}%`,
                    height: "100%",
                    backgroundColor:
                      status.responseWindowProgress >= 80
                        ? "#f56565"
                        : status.responseWindowProgress >= 60
                          ? "#ed8936"
                          : "#48bb78",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>

            {/* Percentage Limit Progress */}
            {config.responsePercentage < 100 && (
              <div style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                  }}
                >
                  <span style={{ color: "#cbd5e0", fontSize: "13px" }}>
                    Percentage Limit
                  </span>
                  <span style={{ color: "#a0aec0", fontSize: "12px" }}>
                    {status.currentPercentage}% / {status.targetPercentage}% of
                    channel messages ({status.totalChannelMessages || 0}) /{" "}
                    {status.windowSeconds}s
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "8px",
                    backgroundColor: "#1a202c",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${status.percentageLimitProgress}%`,
                      height: "100%",
                      backgroundColor:
                        status.percentageLimitProgress >= 80
                          ? "#f56565"
                          : status.percentageLimitProgress >= 60
                            ? "#ed8936"
                            : "#48bb78",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Cooldown Progress */}
            <div style={{ marginBottom: status.isInCooldown ? "16px" : "0" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "4px",
                }}
              >
                <span style={{ color: "#cbd5e0", fontSize: "13px" }}>
                  Cooldown Timer
                </span>
                <span style={{ color: "#a0aec0", fontSize: "12px" }}>
                  {status.isInCooldown
                    ? `${Math.ceil(
                        ((100 - status.cooldownProgress) *
                          config.cooldownSeconds) /
                          100,
                      )}s remaining`
                    : "Ready"}
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "8px",
                  backgroundColor: "#1a202c",
                  borderRadius: "4px",
                  overflow: "hidden",
                  opacity: status.isInCooldown ? 1 : 0.5,
                }}
              >
                <div
                  style={{
                    width: status.isInCooldown
                      ? `${100 - status.cooldownProgress}%`
                      : "0%",
                    height: "100%",
                    backgroundColor: status.isInCooldown
                      ? "#4299e1"
                      : "#718096",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>

            {/* Status Message */}
            {status.isInCooldown && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "8px 12px",
                  backgroundColor: "rgba(245, 101, 101, 0.1)",
                  border: "1px solid rgba(245, 101, 101, 0.3)",
                  borderRadius: "4px",
                }}
              >
                <p
                  style={{
                    fontSize: "12px",
                    color: "#f56565",
                    margin: 0,
                  }}
                >
                  HAL is in cooldown mode and cannot respond to messages.
                </p>
              </div>
            )}
          </div>

          {/* Statistics */}
          <div
            style={{
              borderTop: "1px solid #4a5568",
              paddingTop: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <h4
                style={{
                  color: "#e2e8f0",
                  fontSize: "16px",
                  margin: 0,
                }}
              >
                Statistics
              </h4>
              <select
                value={statsHours}
                onChange={(e) => setStatsHours(parseInt(e.target.value))}
                style={{
                  padding: "8px 12px",
                  backgroundColor: "#2d3748",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23e2e8f0' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  paddingRight: "36px",
                  cursor: "pointer",
                  outline: "none",
                }}
              >
                <option value={1}>Last hour</option>
                <option value={6}>Last 6 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={168}>Last 7 days</option>
              </select>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "12px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#2d3748",
                  padding: "12px",
                  borderRadius: "6px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    color: "#cbd5e0",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Replies Sent
                </div>
                <div
                  style={{
                    color: "#48bb78",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  {totals.messagesSent}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#2d3748",
                  padding: "12px",
                  borderRadius: "6px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    color: "#cbd5e0",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Replies Skipped
                </div>
                <div
                  style={{
                    color: "#f56565",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  {totals.messagesThrottled}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#2d3748",
                  padding: "12px",
                  borderRadius: "6px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    color: "#cbd5e0",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Buffered
                </div>
                <div
                  style={{
                    color: "#4299e1",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  {totals.messagesBuffered}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#2d3748",
                  padding: "12px",
                  borderRadius: "6px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    color: "#cbd5e0",
                    fontSize: "12px",
                    marginBottom: "4px",
                  }}
                >
                  Summaries Sent
                </div>
                <div
                  style={{
                    color: "#9146FF",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  {totals.summariesSent}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
